import { createRouter, createWebHashHistory, RouteMeta, RouteRecordRaw } from 'vue-router';
import Paths, { LOGIN_REQUIRED_PATHS } from '@/constants/Paths';
import PageTitleI18nKeys from '@/constants/PageTitleI18nKeys';
import { useAuthStore } from '@/store/useAuthStore';
import { useCustomerStore } from '@/store/useCustomerStore';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore';
import { MODULE } from '@/constants/Module';
import { loadLocaleMessages } from '@/utils/i18n';
import { showModule } from '@/utils/module';
import isDefined from '@common/utils/isDefined';
import { isBefore } from 'date-fns';
import { SystemConfigurationDto } from '@/api';

type Meta = RouteMeta & {
  canLoad: (systemConfigurationDto?: SystemConfigurationDto) => boolean;
};

const routes = [
  {
    path: '/offenses',
    component: () => import('@/layouts/PublicLayout.vue'),
    redirect: 'search',
    meta: {
      canLoad: (systemConfigurationDto: SystemConfigurationDto) => systemConfigurationDto.allowOffenses,
    },
    children: [
      {
        path: 'search',
        component: () => import('@/pages/public/offenses/OffensesSearchPage.vue'),
      },
      {
        path: ':id',
        component: () => import('@/pages/public/offenses/OffensesListPage.vue'),
      },
      {
        path: ':id/detail',
        component: () => import('@/pages/public/offenses/OffensesDetailPage.vue'),
      },
    ],
  },
  {
    path: Paths.LOGIN,
    component: () => import('@/pages/LoginPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.LOGIN },
  },
  {
    path: Paths.REGISTRATION,
    component: () => import('@/pages/RegistrationPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.REGISTRATION },
  },
  {
    path: Paths.FORGOTTEN_PASSWORD,
    component: () => import('@/pages/ForgottenPasswordPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.FORGOTTEN_PASSWORD },
  },
  {
    path: Paths.VERIFICATION,
    component: () => import('@/pages/VerificationCodePage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.VERIFICATION },
  },
  {
    path: Paths.CHANGE_PASSWORD,
    component: () => import('@/pages/ChangePasswordPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.CHANGE_PASSWORD },
  },
  {
    path: Paths.VERIFY,
    component: () => import('@/pages/VerifyPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.VERIFY },
  },
  {
    path: Paths.VERIFY_PARENT,
    component: () => import('@/pages/VerifyPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.VERIFY },
  },
  {
    path: Paths.APPROVE_MANAGED_CARD,
    component: () => import('@/pages/ApproveManagedCardPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.APPROVE_MANAGED_CARD },
  },
  {
    path: Paths.REJECT_CARD_MANAGE_REQUEST_PAGE,
    component: () => import('@/pages/RejectCardManageRequestPage.vue'),
    meta: { titleI18nKey: PageTitleI18nKeys.REJECT_CARD_MANAGE_REQUEST },
  },
  {
    path: Paths.OAUTH,
    component: () => import('@/layouts/OAuthLayout.vue'),
    children: [
      {
        path: Paths.LINK_ACCOUNT,
        component: () => import('@/pages/LoginPage.vue'),
      },
      {
        path: Paths.REGISTER,
        component: () => import('@/pages/RegistrationPage.vue'),
      },
    ],
  },
  {
    path: Paths.APP,
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: Paths.USER_ACCOUNT,
        component: () => import('@/pages/UserAccountPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.USER_ACCOUNT },
      },
      {
        path: Paths.USER_ACCOUNT_FINISH_ADD_CARD,
        component: () => import('@/pages/UserAccountFinishAddCardPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.USER_ACCOUNT },
      },
      {
        path: Paths.USER_ACCOUNT_FINISH_REGISTER_PORTABLE_CARD,
        component: () => import('@/pages/UserAccountFinishRegisterPortableCardPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.USER_ACCOUNT },
      },
      {
        path: Paths.USER_LINKED_ACCOUNT_WITH_ID,
        component: () => import('@/pages/UserLinkedAccountPage.vue'),
        // title is set dynamically in AppLayout.vue
      },
      {
        path: Paths.USER_LINKED_ACCOUNT,
        component: () => import('@/pages/UserLinkedAccountPage.vue'),
        // title is set dynamically in AppLayout.vue
      },
      {
        path: Paths.ASSIGNED_CARD_LIST,
        component: () => import('@/pages/AssignedCardListPage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.ASSIGNED_CARD_LIST,
          canLoad: () => showModule(MODULE.ASSIGNED_CARD_LIST),
        },
      },
      {
        path: Paths.MANAGED_CARD_LIST,
        component: () => import('@/pages/ManagedCardListPage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.MANAGED_CARD_LIST,
          canLoad: () => showModule(MODULE.MANAGED_CARD_LIST),
        },
      },
      {
        path: Paths.CARD_MANAGING_ACCOUNT_LIST,
        component: () => import('@/pages/CardManagingAccountListPage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.CARD_MANAGING_ACCOUNT_LIST,
          canLoad: () => showModule(MODULE.CARD_MANAGING_ACCOUNT_LIST),
        },
      },
      {
        path: Paths.NOTIFICATION_SETTING,
        component: () => import('@/pages/NotificationSettingPage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.NOTIFICATION_SETTING,
          canLoad: () => showModule(MODULE.NOTIFICATION_SETTING),
        },
      },
      {
        path: Paths.TICKET_FARE,
        component: () => import('@/pages/TicketFarePage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.TICKET_FARE,
          canLoad: () => showModule(MODULE.TICKET_FARE),
        },
      },
      {
        path: Paths.ELECTRONIC_WALLET,
        component: () => import('@/pages/ElectronicWalletPage.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.ELECTRONIC_WALLET,
          canLoad: () => showModule(MODULE.ELECTRONIC_WALLET),
        },
      },
      {
        path: Paths.SHOPPING_CART,
        component: () => import('@/pages/ShoppingCartPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.SHOPPING_CART },
      },
      {
        path: Paths.ORDER_LIST,
        component: () => import('@/pages/OrderListPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.ORDER_LIST },
      },
      {
        path: Paths.ACCOUNT_SETTING,
        component: () => import('@/pages/AccountSettingPage.vue'),
        meta: { titleI18nKey: PageTitleI18nKeys.ACCOUNT_SETTING },
      },
      {
        path: Paths.ENVIRONMENT_VARIABLES,
        component: () => import('@/pages/admin/EnvironmentVariables.vue'),
        meta: {
          titleI18nKey: PageTitleI18nKeys.ENVIRONMENT_VARIABLES,
          canLoad: () => import.meta.env.DEV,
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes: routes as RouteRecordRaw[],
});

let whenLocaleMessagesLoaded: Promise<void> | null = null;

router.beforeEach((_to, _from, next) => {
  whenLocaleMessagesLoaded ??= loadLocaleMessages();

  whenLocaleMessagesLoaded.then(
    () => {
      next();
    },
    error => {
      next(error);
    },
  );
});

router.beforeEach(async (to, _from, next) => {
  if (!to.matched[0]) {
    return next();
  }

  const authStore = useAuthStore();
  const customerStore = useCustomerStore();
  const systemConfigurationStore = useSystemConfigurationStore();

  if (authStore.tokenExpiresIn && isBefore(authStore.tokenExpiresIn, new Date())) {
    authStore.logout();
  } else if (authStore.logged && to.matched[0].path === '') {
    return next(Paths.USER_ACCOUNT);
  }

  if (LOGIN_REQUIRED_PATHS.includes(to.matched[0].path) && !authStore.logged) {
    return next({
      path: Paths.LOGIN,
      query: { redirectUrl: to.path, token: to.query?.token },
    });
  }

  if (
    to.matched[0].path === Paths.APP &&
    authStore.logged &&
    isDefined(to.meta?.canLoad) &&
    !(to.meta as Meta).canLoad(systemConfigurationStore.configuration)
  ) {
    return next({
      path: Paths.USER_ACCOUNT,
    });
  }

  if (!to.path.includes(Paths.USER_LINKED_ACCOUNT)) {
    customerStore.clearLinkedCustomer();
  }

  // if there are query parameters before '/#' move them to the end
  if (globalThis.location.search) {
    globalThis.location.replace(`/#${to.fullPath}${globalThis.location.search}`);
  }

  next();
});

export default router;
